import { useState, useRef, useEffect } from 'react';

interface TagInputWithSuggestionsProps {
  label: string;
  suggestions: string[];
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

// WHY: Free-text multi-value input (languages, tool proficiencies) with PHB
// typeahead. Suggestions are a static list; custom values are always allowed
// (homebrew). Selection is order-preserving and case-insensitively de-duplicated.
export default function TagInputWithSuggestions({
  label,
  suggestions,
  values,
  onChange,
  placeholder,
}: TagInputWithSuggestionsProps) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const trimmed = text.trim();
  const lowerValues = values.map((v) => v.toLowerCase());
  const filtered = trimmed
    ? suggestions.filter(
        (s) => s.toLowerCase().includes(trimmed.toLowerCase()) && !lowerValues.includes(s.toLowerCase())
      )
    : [];

  // WHY: Close the suggestion dropdown on outside click so it never traps focus
  // or lingers over other form sections on mobile.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function addValue(raw: string) {
    const val = raw.trim();
    if (!val) return;
    // WHY: Case-insensitive dedupe so "Common" can't be added twice, but the
    // originally-cased first entry is preserved.
    if (values.some((v) => v.toLowerCase() === val.toLowerCase())) {
      setText('');
      setOpen(false);
      return;
    }
    onChange([...values, val]);
    setText('');
    setOpen(false);
    setActiveIndex(0);
  }

  function removeValue(val: string) {
    onChange(values.filter((v) => v !== val));
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (open && filtered.length > 0) {
        addValue(filtered[activeIndex]);
      } else {
        addValue(text);
      }
    } else if (e.key === 'Backspace' && trimmed === '' && values.length > 0) {
      // WHY: Backspace on an empty input removes the last chip — a familiar
      // tag-input affordance that avoids hunting for the small × button.
      removeValue(values[values.length - 1]);
    } else if (e.key === 'ArrowDown' && open && filtered.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % filtered.length);
    } else if (e.key === 'ArrowUp' && open && filtered.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap items-center gap-2 p-2 panel">
        {values.map((val) => (
          <span
            key={val}
            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-dnd-panel border border-dnd-border text-sm text-dnd-fg"
          >
            {val}
            <button
              type="button"
              aria-label={`Remove ${val}`}
              onClick={() => removeValue(val)}
              className="text-dnd-muted hover:text-dnd-danger-text text-lg leading-none min-h-[32px] min-w-[32px] flex items-center justify-center"
            >
              &times;
            </button>
          </span>
        ))}
        <input
          type="text"
          aria-label={label}
          value={text}
          placeholder={placeholder}
          autoComplete="off"
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActiveIndex(0);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="flex-1 min-w-[120px] bg-transparent border-none outline-none text-dnd-input-text placeholder-dnd-placeholder py-1"
        />
      </div>

      {(open && filtered.length > 0) || (trimmed && !suggestions.some((s) => s.toLowerCase() === trimmed.toLowerCase())) ? (
        // WHY: On mobile the tag field often sits at the bottom of the form, so the
        // on-screen keyboard covers anything rendered *below* the input. Positioning
        // the suggestion list ABOVE the input (`bottom-full`) keeps it visible above
        // the keyboard. `z-20` ensures it overlays the cards above it.
        <ul className="absolute z-20 left-0 right-0 bottom-full mb-2 max-h-56 overflow-auto bg-dnd-panel border border-dnd-border rounded shadow-lg">
          {filtered.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                // WHY: Keyboard arrow navigation highlights an option; clicking or
                // Enter on it adds it. Mouse users just tap the row.
                className={`w-full text-left px-3 py-2 text-sm text-dnd-fg hover:bg-dnd-darker ${
                  i === activeIndex ? 'bg-dnd-darker' : ''
                }`}
                onMouseEnter={() => setActiveIndex(i)}
                onClick={() => addValue(s)}
              >
                {s}
              </button>
            </li>
          ))}
          {trimmed && !suggestions.some((s) => s.toLowerCase() === trimmed.toLowerCase()) && (
            <li>
              <button
                type="button"
                className="w-full text-left px-3 py-2 text-sm text-dnd-muted hover:bg-dnd-darker italic border-t border-dnd-border"
                onClick={() => addValue(text)}
              >
                Add &ldquo;{trimmed}&rdquo; as custom
              </button>
            </li>
          )}
        </ul>
      ) : null}
    </div>
  );
}
